export const isIOSLikeNavigator = (navigatorLike = {}) => {
  const userAgent = String(navigatorLike.userAgent || "");
  const platform = String(navigatorLike.platform || "");
  const maxTouchPoints = Number(navigatorLike.maxTouchPoints || 0);
  return /iPad|iPhone|iPod/.test(userAgent)
    || (platform === "MacIntel" && maxTouchPoints > 1);
};

export const isCurrentIOSLikeBrowser = () => {
  if (typeof window === "undefined") return false;
  return isIOSLikeNavigator(window.navigator || {});
};


export const getVoiceBrowserFamily = (navigatorLike = {}) => {
  const userAgent = String(navigatorLike.userAgent || "");
  if (/FxiOS|Firefox\//i.test(userAgent)) return "firefox";
  if (/CriOS|Chrome\//i.test(userAgent)) return "chrome";
  if (/EdgiOS|Edg\//i.test(userAgent)) return "edge";
  if (/OPiOS|OPR\//i.test(userAgent)) return "opera";
  if (/Safari\//i.test(userAgent) && /Version\//i.test(userAgent)) return "safari";
  return "unknown";
};

export const resolveVoiceCaptureStrategy = ({
  navigatorLike = {},
  hasSpeechRecognition = false,
  hasMediaDevices = false,
  hasMediaRecorder = false,
} = {}) => {
  if (isIOSLikeNavigator(navigatorLike) && hasSpeechRecognition) return "ios-native";
  if (hasMediaDevices && hasMediaRecorder) return "media-recorder";
  return "unsupported";
};


export const mergeSpeechRecognitionResults = (previousSegments = [], eventLike = {}) => {
  const results = Array.from(eventLike.results || []);
  const segments = previousSegments.map((segment) => ({ ...segment }));

  // Read every result currently exposed by the browser. Safari can advance
  // resultIndex while also promoting an earlier interim segment to final.
  // Replacing by result index keeps revisions idempotent and avoids duplicates.
  for (let index = 0; index < results.length; index += 1) {
    const result = results[index];
    segments[index] = {
      transcript: String(result?.[0]?.transcript || "").trim(),
      isFinal: Boolean(result?.isFinal),
    };
  }

  // SpeechRecognitionEvent.results is the complete current session snapshot.
  // Trailing interim results may be removed when the browser revises the
  // hypothesis, so stale segments beyond the current list length must be dropped.
  segments.length = results.length;
  const spokenSegments = segments.filter((segment) => Boolean(segment?.transcript));
  const transcript = spokenSegments.map((segment) => segment.transcript).join(" ").replace(/\s+/g, " ").trim();
  const hasFinalResult = spokenSegments.some((segment) => segment.isFinal);
  const hasInterimResult = spokenSegments.some((segment) => !segment.isFinal);

  return {
    segments,
    transcript,
    hasFinalResult,
    hasInterimResult,
    isComplete: Boolean(transcript) && hasFinalResult && !hasInterimResult,
  };
};

export const shouldRetryBasicAudioCapture = (errorLike = {}) => {
  const name = String(errorLike?.name || "");
  return ["OverconstrainedError", "ConstraintNotSatisfiedError", "NotSupportedError", "TypeError"].includes(name);
};

export const classifySpeechRecognitionError = (errorCode = "") => {
  const code = String(errorCode || "").toLowerCase();
  if (code === "no-speech") return "NO_AUDIO";
  if (["not-allowed", "service-not-allowed"].includes(code)) return "MIC_PERMISSION_DENIED";
  if (code === "audio-capture") return "MIC_CAPTURE_FAILED";
  if (code === "network") return "NETWORK_ERROR";
  if (code === "language-not-supported") return "LANGUAGE_NOT_SUPPORTED";
  if (code === "aborted") return "RECOGNITION_ABORTED";
  return "STT_FAILED";
};

export const shouldStopContinuousRecognitionAfterError = (errorCode = "") => (
  String(errorCode || "") !== "NO_AUDIO"
);

export const getVoiceRecorderMimeCandidates = (isIOSLike = false) => (
  isIOSLike
    ? ["audio/mp4", "audio/mpeg", "audio/webm;codecs=opus", "audio/webm"]
    : ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/ogg", "audio/mp4", "audio/mpeg"]
);

export const getVoiceAudioFormatForMime = (mime = "") => {
  const value = String(mime || "").toLowerCase();
  if (value.includes("mp4")) return "m4a";
  if (value.includes("mpeg")) return "mp3";
  if (value.includes("wav")) return "wav";
  if (value.includes("ogg")) return "ogg";
  return "webm";
};
